import { useEffect, useRef, useState } from "react";
import { usePlanner, formatArea, formatDistance } from "@/features/space-designer/lib/store";
import { ItemShape } from "./ItemShape";
import { polygonArea, polygonCentroid, polygonPerimeter } from "@/features/space-designer/lib/wall-detector";
import { useSvgLibrary } from "@/features/space-designer/lib/svg-library";
import { toast } from "sonner";
import { snapChairToSeat } from "@/features/space-designer/lib/snap";
import { isWorkstationShape } from "@/features/space-designer/lib/workstation-layout";
import { teamFor, seatCodeFor } from "@/features/space-designer/lib/allocation";
import type { WallMaterial } from "@/features/space-designer/lib/types";
import { ClusterEditor } from "./ClusterEditor";

// Stroke colour per wall construction type. Cemented falls back to the theme
// token; the others get distinct hues so the floor reads at a glance.
function wallStroke(material?: WallMaterial): string {
  switch (material) {
    case "glass": return "#38bdf8";   // sky blue
    case "gypsum": return "#94a3b8";  // slate gray
    case "wooden": return "#b45309";  // wood brown
    default: return "var(--wall-color)"; // cemented (dark)
  }
}

interface DragState {
  kind: "move" | "resize" | "rotate" | "pan" | "bp-move" | "bp-resize" | "wall-endpoint";
  ids?: string[];
  startX: number;
  startY: number;
  origin: Record<string, { x: number; y: number; w: number; h: number; rotation: number }>;
  handle?: string;
  panStart?: { x: number; y: number };
  bpOrigin?: { x: number; y: number; w: number; h: number };
  wallOrigin?: { id: string; x1: number; y1: number; x2: number; y2: number; end: "a" | "b" };
}

export function Canvas() {
  const svgRef = useRef<SVGSVGElement>(null);
  const {
    scene,
    tool,
    unit,
    gridSize,
    snap,
    snapSettings,
    layers,
    selectedIds,
    zoom,
    pan,
    pendingPoint,
    wallDraft,
    pendingAreaPoints,
    setPan,
    setZoom,
    addFromCatalog,
    addCustomSvg,
    updateItem,
    updateItems,
    persistScene,
    toggleSelect,
    select,
    clearSelection,
    pushHistory,
    startWallPoint,
    commitWall,
    commitMeasurement,
    addAreaPoint,
    finishArea,
    removeArea,
    cancelPending,
    removeWall,
    updateWall,
    removeMeasurement,
    removeItem,
    explodeItem,
    blueprintSelected,
    selectBlueprint,
    updateBlueprint,
    setBlueprint,
    wallCandidates,
  } = usePlanner();
  const getSvgAsset = useSvgLibrary((s) => s.assets);

  const [drag, setDrag] = useState<DragState | null>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const [shiftHeld, setShiftHeld] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [clusterEditorRoomId, setClusterEditorRoomId] = useState<string | null>(null);
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number; additive: boolean } | null>(null);
  const autoRecoveredView = useRef(false);
  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!mounted || autoRecoveredView.current) return;
    const bounds = getSceneBounds(scene);
    const svg = svgRef.current;
    const rect = svg?.getBoundingClientRect();
    if (!bounds || !rect?.width || !rect?.height) return;
    autoRecoveredView.current = true;

    const visible = {
      minX: (0 - pan.x) / zoom,
      minY: (0 - pan.y) / zoom,
      maxX: (rect.width - pan.x) / zoom,
      maxY: (rect.height - pan.y) / zoom,
    };
    const overlapsView = bounds.maxX >= visible.minX && bounds.minX <= visible.maxX && bounds.maxY >= visible.minY && bounds.minY <= visible.maxY;
    if (overlapsView) return;

    const padding = 80;
    const contentW = Math.max(1, bounds.maxX - bounds.minX);
    const contentH = Math.max(1, bounds.maxY - bounds.minY);
    const nextZoom = Math.max(0.2, Math.min(2.5, Math.min((rect.width - padding * 2) / contentW, (rect.height - padding * 2) / contentH)));
    setZoom(nextZoom);
    setPan({
      x: rect.width / 2 - ((bounds.minX + bounds.maxX) / 2) * nextZoom,
      y: rect.height / 2 - ((bounds.minY + bounds.maxY) / 2) * nextZoom,
    });
  }, [mounted, scene, pan.x, pan.y, zoom, setPan, setZoom]);

  function toWorld(clientX: number, clientY: number) {
    const svg = svgRef.current!;
    const rect = svg.getBoundingClientRect();
    const x = (clientX - rect.left - pan.x) / zoom;
    const y = (clientY - rect.top - pan.y) / zoom;
    return { x, y };
  }

  function snapVal(v: number) {
    return snap && snapSettings.grid ? Math.round(v / gridSize) * gridSize : v;
  }

  function snapPoint(p: { x: number; y: number }) {
    let pt = { x: snapVal(p.x), y: snapVal(p.y) };
    if (snapSettings.vertex) {
      const tol = 18 / zoom;
      let best: { x: number; y: number; d: number } | null = null;
      for (const w of scene.walls) {
        for (const ep of [{ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }]) {
          const d = Math.hypot(ep.x - p.x, ep.y - p.y);
          if (d < tol && (!best || d < best.d)) best = { x: ep.x, y: ep.y, d };
        }
      }
      if (best) pt = { x: best.x, y: best.y };
    }
    return pt;
  }

  // Snap the segment to 15° increments when the Angle-snap toggle is on OR the
  // user is holding Shift (force) — Shift gives an on-demand straight line.
  function angleSnap(start: { x: number; y: number }, end: { x: number; y: number }, force = false) {
    if (!snapSettings.angle && !force) return end;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const len = Math.hypot(dx, dy);
    if (len < 1) return end;
    const ang = Math.atan2(dy, dx);
    const step = Math.PI / 12; // 15°
    const snapped = Math.round(ang / step) * step;
    return { x: start.x + Math.cos(snapped) * len, y: start.y + Math.sin(snapped) * len };
  }

  // Returns true if the wall midpoint lies over the (unrotated) blueprint footprint.
  function isOverBlueprint(x: number, y: number): boolean {
    const bp = scene.blueprint;
    if (!bp) return false;
    return x >= bp.x && x <= bp.x + bp.w && y >= bp.y && y <= bp.y + bp.h;
  }

  function eraseWallWithWarn(id: string) {
    const w = scene.walls.find((x) => x.id === id);
    if (w) {
      const mx = (w.x1 + w.x2) / 2;
      const my = (w.y1 + w.y2) / 2;
      if (isOverBlueprint(mx, my) && scene.blueprint && layers.blueprint) {
        toast.warning("Wall erased over blueprint", {
          description: "Blueprint content underneath stays visible. Use Undo (Ctrl+Z) if this wasn't intended.",
        });
      }
    }
    removeWall(id);
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.target as HTMLElement)?.tagName === "INPUT" || (e.target as HTMLElement)?.tagName === "TEXTAREA") return;
      if (e.key === "Escape") {
        cancelPending();
        clearSelection();
      }
      if ((e.key === "Enter" || e.key === " ") && tool === "area" && pendingAreaPoints.length >= 3) {
        e.preventDefault();
        finishArea();
      }
      // Shift+X — explode selected workstation
      if (e.shiftKey && (e.key === "X" || e.key === "x")) {
        const ids = usePlanner.getState().selectedIds;
        if (ids.length === 1) {
          const it = usePlanner.getState().scene.items.find((i) => i.id === ids[0]);
          if (it && isWorkstationShape(it.shape)) {
            e.preventDefault();
            explodeItem(it.id);
          }
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cancelPending, clearSelection, tool, pendingAreaPoints.length, finishArea, explodeItem]);

  function onWheel(e: React.WheelEvent) {
    // Ctrl/Meta + wheel zooms around the cursor; plain wheel pans infinitely.
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.05, Math.min(8, zoom * factor));
      const rect = svgRef.current!.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      // Keep the world point under the cursor stationary.
      const wx = (cx - pan.x) / zoom;
      const wy = (cy - pan.y) / zoom;
      setZoom(newZoom);
      setPan({ x: cx - wx * newZoom, y: cy - wy * newZoom });
      return;
    }
    // Pan with wheel (supports trackpad two-finger scroll on both axes).
    e.preventDefault();
    const dx = e.shiftKey && e.deltaX === 0 ? e.deltaY : e.deltaX;
    const dy = e.shiftKey && e.deltaX === 0 ? 0 : e.deltaY;
    setPan({ x: pan.x - dx, y: pan.y - dy });
  }

  function onPointerDownBg(e: React.PointerEvent) {
    if (e.button === 1 || tool === "pan" || (e.button === 0 && e.altKey)) {
      setDrag({ kind: "pan", startX: e.clientX, startY: e.clientY, origin: {}, panStart: { ...pan } });
      (e.target as Element).setPointerCapture?.(e.pointerId);
      return;
    }
    let p = snapPoint(toWorld(e.clientX, e.clientY));
    if (tool === "erase") {
      // background click: do nothing (children handle their own delete)
      return;
    }
    if (tool === "wall") {
      if (!pendingPoint) startWallPoint(p.x, p.y);
      else {
        p = angleSnap(pendingPoint, p, e.shiftKey);
        commitWall(p.x, p.y);
      }
      return;
    }
    if (tool === "measure") {
      if (pendingPoint) p = angleSnap(pendingPoint, p, e.shiftKey);
      commitMeasurement(p.x, p.y);
      return;
    }
    if (tool === "area") {
      addAreaPoint(p.x, p.y);
      return;
    }
    if (tool === "select") {
      // Begin marquee selection (rubber-band). Click without drag still clears.
      const wp = toWorld(e.clientX, e.clientY);
      setMarquee({ x0: wp.x, y0: wp.y, x1: wp.x, y1: wp.y, additive: e.shiftKey });
      (e.target as Element).setPointerCapture?.(e.pointerId);
    }
  }

  function onDoubleClickBg() {
    if (tool === "area" && pendingAreaPoints.length >= 3) finishArea();
  }

  function onPointerMove(e: React.PointerEvent) {
    const p = toWorld(e.clientX, e.clientY);
    setCursor(p);
    if (e.shiftKey !== shiftHeld) setShiftHeld(e.shiftKey);
    if (marquee) {
      setMarquee({ ...marquee, x1: p.x, y1: p.y });
      return;
    }
    if (!drag) return;
    if (drag.kind === "pan" && drag.panStart) {
      setPan({ x: drag.panStart.x + (e.clientX - drag.startX), y: drag.panStart.y + (e.clientY - drag.startY) });
      return;
    }
    const dx = (e.clientX - drag.startX) / zoom;
    const dy = (e.clientY - drag.startY) / zoom;
    if (drag.kind === "wall-endpoint" && drag.wallOrigin) {
      const o = drag.wallOrigin;
      let nx = (o.end === "a" ? o.x1 : o.x2) + dx;
      let ny = (o.end === "a" ? o.y1 : o.y2) + dy;
      // Angle-snap relative to the fixed endpoint, then snap grid/vertex.
      const fixed = o.end === "a" ? { x: o.x2, y: o.y2 } : { x: o.x1, y: o.y1 };
      const angled = angleSnap(fixed, { x: nx, y: ny }, e.shiftKey);
      const sp = snapPoint(angled);
      nx = sp.x; ny = sp.y;
      if (o.end === "a") updateWall(o.id, { x1: nx, y1: ny });
      else updateWall(o.id, { x2: nx, y2: ny });
      return;
    }
    if (drag.kind === "bp-move" && drag.bpOrigin) {
      const o = drag.bpOrigin;
      updateBlueprint({ x: snap ? Math.round((o.x + dx) / gridSize) * gridSize : o.x + dx, y: snap ? Math.round((o.y + dy) / gridSize) * gridSize : o.y + dy });
      return;
    }
    if (drag.kind === "bp-resize" && drag.bpOrigin && drag.handle) {
      const o = drag.bpOrigin;
      const ratio = o.h / o.w;
      let w = o.w;
      if (drag.handle.includes("e")) w = Math.max(50, o.w + dx);
      if (drag.handle.includes("w")) w = Math.max(50, o.w - dx);
      const h = w * ratio;
      let x = o.x;
      let y = o.y;
      if (drag.handle.includes("w")) x = o.x + (o.w - w);
      if (drag.handle.includes("n")) y = o.y + (o.h - h);
      updateBlueprint({ x, y, w, h });
      return;
    }
    if (drag.kind === "move" && drag.ids) {
      // Ignore micro-movements from a simple click so re-selecting a chair
      // doesn't trigger seat snap and reset its rotation.
      if (Math.abs(e.clientX - drag.startX) < 3 && Math.abs(e.clientY - drag.startY) < 3) return;
      const effectiveIds = (() => {
        const expanded = new Set(drag.ids);
        for (const id of drag.ids) {
          const item = scene.items.find((i) => i.id === id);
          if (item?.kind === "room" && item.groupId) {
            scene.items.forEach((i) => { if (i.groupId === item.groupId) expanded.add(i.id); });
          }
        }
        return Array.from(expanded);
      })();
      const getOrigin = (id: string) => drag.origin[id];
      const movingIds = new Set(effectiveIds);
      const movingWholeGroup = effectiveIds.length > 1;
      const movingGroupIds = new Set(
        scene.items.filter((i) => movingIds.has(i.id) && i.groupId).map((i) => i.groupId as string),
      );
      // Items that participate in collision (exclude rooms/architectural containers)
      const blockers = scene.items.filter(
        (it) =>
          !movingIds.has(it.id) &&
          it.kind !== "room" &&
          it.kind !== "architectural" &&
          !(movingWholeGroup && it.groupId && movingGroupIds.has(it.groupId)),
      );
      const overlaps = (ax: number, ay: number, aw: number, ah: number, bx: number, by: number, bw: number, bh: number) =>
        ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
      // If a composed/clustered piece starts out touching or overlapping another
      // piece (e.g. the L-shaped executive desk worktop over its storage return),
      // don't let that existing overlap trap the dragged item in place.
      const initiallyOverlappingBlockerIds = new Set<string>();
      blockers.forEach((b) => {
        for (const id of effectiveIds) {
          const o = getOrigin(id);
          if (o && overlaps(o.x, o.y, o.w, o.h, b.x, b.y, b.w, b.h)) {
            initiallyOverlappingBlockerIds.add(b.id);
            break;
          }
        }
      });
      // Build candidates for full, x-only, and y-only deltas so the user can
      // slide along a blocker instead of getting completely stuck on contact.
      // For multi-item groups, compute the snapped delta ONCE from the primary
      // item so every child translates by the exact same offset (otherwise
      // independent per-item snapping causes the cluster to jitter apart).
      const primaryId = drag.ids![0];
      const primaryOrigin = getOrigin(primaryId)!;
      const primaryItem = scene.items.find((i) => i.id === primaryId)!;
      const buildCandidates = (useDx: number, useDy: number) => {
        let appliedDx = useDx;
        let appliedDy = useDy;
        const freePositionChair =
          primaryItem.shape === "chair" && !!primaryItem.groupId && effectiveIds.length === 1 && !e.shiftKey;
        if (snap && !freePositionChair) {
          const nxPrim = Math.round((primaryOrigin.x + useDx) / gridSize) * gridSize;
          const nyPrim = Math.round((primaryOrigin.y + useDy) / gridSize) * gridSize;
          appliedDx = nxPrim - primaryOrigin.x;
          appliedDy = nyPrim - primaryOrigin.y;
        }
        return effectiveIds.map((id) => {
          const o = getOrigin(id)!;
          const it = scene.items.find((i) => i.id === id)!;
          return { id, x: o.x + appliedDx, y: o.y + appliedDy, w: it.w, h: it.h, kind: it.kind };
        });
      };
      const collides = (cands: { x: number; y: number; w: number; h: number; kind: string }[]) =>
        cands.some((c) => {
          if (c.kind === "room" || c.kind === "architectural") return false;
          return blockers.some((b) => !initiallyOverlappingBlockerIds.has(b.id) && overlaps(c.x, c.y, c.w, c.h, b.x, b.y, b.w, b.h));
        });
      let candidates = buildCandidates(dx, dy);
      if (collides(candidates)) {
        const xOnly = buildCandidates(dx, 0);
        const yOnly = buildCandidates(0, dy);
        const xOk = !collides(xOnly);
        const yOk = !collides(yOnly);
        if (xOk && yOk) candidates = Math.abs(dx) >= Math.abs(dy) ? xOnly : yOnly;
        else if (xOk) candidates = xOnly;
        else if (yOk) candidates = yOnly;
        else return;
      }

      // Single-chair seat snap is intentional only: hold Shift near a seat slot.
      // Normal chair drag stays smooth/free so the chair can sit exactly where the user drops it.
      if (candidates.length === 1) {
        const c = candidates[0];
        const it = scene.items.find((i) => i.id === c.id);
        if (it && it.shape === "chair" && e.shiftKey && !e.altKey) {
          const cx = c.x + c.w / 2;
          const cy = c.y + c.h / 2;
          const o = drag.origin[c.id];
          const originCenter = o ? { x: o.x + o.w / 2, y: o.y + o.h / 2 } : undefined;
          const snapped = snapChairToSeat(it.id, cx, cy, scene.items, 30, originCenter);
          if (snapped) {
            c.x = snapped.x - snapped.w / 2;
            c.y = snapped.y - snapped.h / 2;
            const patch: { x: number; y: number; rotation: number } = { x: c.x, y: c.y, rotation: snapped.rot };
            updateItem(c.id, patch, { persist: false });
            return;
          }
        }
      }
      updateItems(candidates.map((c) => ({ id: c.id, patch: { x: c.x, y: c.y } })), { persist: false });
    } else if (drag.kind === "resize" && drag.ids && drag.handle) {
      const id = drag.ids[0];
      const o = drag.origin[id];
      // Transform the world-space delta into the item's local (unrotated) frame
      // so that n/s handles always resize height and e/w handles always resize width,
      // regardless of the item's rotation.
      const rad = (-o.rotation * Math.PI) / 180;
      const cosR = Math.cos(rad);
      const sinR = Math.sin(rad);
      const ldx = dx * cosR - dy * sinR;
      const ldy = dx * sinR + dy * cosR;
      let { x, y, w, h } = o;
      if (drag.handle.includes("e")) w = Math.max(20, o.w + ldx);
      if (drag.handle.includes("s")) h = Math.max(20, o.h + ldy);
      if (drag.handle.includes("w")) {
        w = Math.max(20, o.w - ldx);
        x = o.x + (o.w - w);
      }
      if (drag.handle.includes("n")) {
        h = Math.max(20, o.h - ldy);
        y = o.y + (o.h - h);
      }
      if (snap) {
        w = Math.max(gridSize, Math.round(w / gridSize) * gridSize);
        h = Math.max(gridSize, Math.round(h / gridSize) * gridSize);
      }
      // Keep the opposite edge anchored in WORLD space when the item is rotated.
      // Rotation pivots around the original center (o.w/2, o.h/2), so shrinking
      // from a single edge would otherwise visually drift. We compensate by
      // offsetting (x, y) so the rotated center moves to match the new center.
      if (o.rotation % 360 !== 0) {
        const origCx = o.x + o.w / 2;
        const origCy = o.y + o.h / 2;
        // Desired new center in local space (anchor opposite edge in local coords)
        const localNewCx = x + w / 2;
        const localNewCy = y + h / 2;
        const localDcx = localNewCx - (o.x + o.w / 2);
        const localDcy = localNewCy - (o.y + o.h / 2);
        // Rotate local center delta back into world space
        const rad2 = (o.rotation * Math.PI) / 180;
        const c2 = Math.cos(rad2);
        const s2 = Math.sin(rad2);
        const worldDcx = localDcx * c2 - localDcy * s2;
        const worldDcy = localDcx * s2 + localDcy * c2;
        const newCx = origCx + worldDcx;
        const newCy = origCy + worldDcy;
        x = newCx - w / 2;
        y = newCy - h / 2;
      }
      // If this resize also covers grouped children (room cluster), scale them
      // proportionally relative to the room's original rect.
      if (drag.ids.length > 1) {
        const sx = w / o.w;
        const sy = h / o.h;
        const patches: { id: string; patch: Partial<typeof o> }[] = [
          { id, patch: { x, y, w, h } },
        ];
        for (const cid of drag.ids) {
          if (cid === id) continue;
          const co = drag.origin[cid];
          patches.push({
            id: cid,
            patch: {
              x: x + (co.x - o.x) * sx,
              y: y + (co.y - o.y) * sy,
              w: co.w * sx,
              h: co.h * sy,
            },
          });
        }
        updateItems(patches, { persist: false });
      } else {
        updateItem(id, { x, y, w, h }, { persist: false });
      }
    } else if (drag.kind === "rotate" && drag.ids) {
      const id = drag.ids[0];
      const o = drag.origin[id];
      const cx = o.x + o.w / 2;
      const cy = o.y + o.h / 2;
      const angle = (Math.atan2(p.y - cy, p.x - cx) * 180) / Math.PI + 90;
      const snapped = snap ? Math.round(angle / 15) * 15 : Math.round(angle);
      updateItem(id, { rotation: snapped });
    }
  }

  function onPointerUp() {
    if (marquee) {
      const minX = Math.min(marquee.x0, marquee.x1);
      const maxX = Math.max(marquee.x0, marquee.x1);
      const minY = Math.min(marquee.y0, marquee.y1);
      const maxY = Math.max(marquee.y0, marquee.y1);
      const dragged = Math.hypot(maxX - minX, maxY - minY) > 4 / zoom;
      if (!dragged) {
        if (!marquee.additive) clearSelection();
      } else {
        const hit = scene.items.filter((it) => {
          // Skip locked layers similar to other interactions; simple bbox test.
          return it.x < maxX && it.x + it.w > minX && it.y < maxY && it.y + it.h > minY;
        });
        const ids = new Set(marquee.additive ? selectedIds : []);
        for (const it of hit) {
          ids.add(it.id);
          if (it.groupId) {
            scene.items.forEach((o) => { if (o.groupId === it.groupId) ids.add(o.id); });
          }
        }
        select(Array.from(ids));
      }
      setMarquee(null);
    }
    if (drag) {
      persistScene();
      setDrag(null);
    }
  }

  function startMove(e: React.PointerEvent, id: string) {
    e.stopPropagation();
    if (tool === "erase") {
      removeItem(id);
      return;
    }
    if (tool !== "select") return;
    const clicked = scene.items.find((i) => i.id === id);
    const additive = e.shiftKey;
    let ids: string[];
    let dragIds: string[] | null = null;
    // Group-aware selection: chairs from an exploded workstation are individual
    // by default for precise spacing/orientation edits; click the desk body to move the group.
    if (clicked?.kind === "room") {
      // Rooms are selected individually so resize handles appear and the room
      // can be resized — its cluster children scale proportionally via startResize.
      ids = [id];
      select(ids);
      if (clicked.groupId) {
        const groupedIds = scene.items.filter((i) => i.groupId === clicked.groupId).map((i) => i.id);
        dragIds = groupedIds.length > 1 ? groupedIds : null;
      }
    } else if (clicked?.groupId && (clicked.shape === "chair" || clicked.shape === "door") && !additive) {
      ids = [id];
      select(ids);
    } else if (clicked?.groupId && clicked.shape !== "chair" && !e.altKey) {
      ids = scene.items.filter((i) => i.groupId === clicked.groupId).map((i) => i.id);
      select(ids);
    } else if (!selectedIds.includes(id)) {
      toggleSelect(id, additive);
      ids = additive ? [...selectedIds, id] : [id];
    } else {
      ids = selectedIds;
    }
    pushHistory();
    let idsToMove = dragIds ?? ids;
    const primary = scene.items.find((i) => i.id === id);
    if (primary?.groupId && primary.kind === "room") {
      const groupedIds = scene.items.filter((i) => i.groupId === primary.groupId).map((i) => i.id);
      if (groupedIds.length > 1) idsToMove = groupedIds;
    }
    const origin: DragState["origin"] = {};
    scene.items.forEach((it) => {
      if (idsToMove.includes(it.id)) origin[it.id] = { x: it.x, y: it.y, w: it.w, h: it.h, rotation: it.rotation };
    });
    setDrag({ kind: "move", ids: idsToMove, startX: e.clientX, startY: e.clientY, origin });
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }

  function startResize(e: React.PointerEvent, id: string, handle: string) {
    e.stopPropagation();
    pushHistory();
    const it = scene.items.find((i) => i.id === id)!;
    const origin: Record<string, { x: number; y: number; w: number; h: number; rotation: number }> = {
      [id]: { x: it.x, y: it.y, w: it.w, h: it.h, rotation: it.rotation },
    };
    const ids = [id];
    // Room with a cluster: also capture children so they scale proportionally.
    if (it.kind === "room" && it.groupId) {
      for (const other of scene.items) {
        if (other.id !== id && other.groupId === it.groupId) {
          ids.push(other.id);
          origin[other.id] = { x: other.x, y: other.y, w: other.w, h: other.h, rotation: other.rotation };
        }
      }
    }
    setDrag({
      kind: "resize",
      ids,
      startX: e.clientX,
      startY: e.clientY,
      origin,
      handle,
    });
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }

  function startRotate(e: React.PointerEvent, id: string) {
    e.stopPropagation();
    pushHistory();
    const it = scene.items.find((i) => i.id === id)!;
    setDrag({
      kind: "rotate",
      ids: [id],
      startX: e.clientX,
      startY: e.clientY,
      origin: { [id]: { x: it.x, y: it.y, w: it.w, h: it.h, rotation: it.rotation } },
    });
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
  }
  function onDrop(e: React.DragEvent) {
    const svgId = e.dataTransfer.getData("custom-svg-id");
    if (svgId) {
      const asset = getSvgAsset.find((a) => a.id === svgId);
      if (asset) {
        const p = snapPoint(toWorld(e.clientX, e.clientY));
        addCustomSvg(p.x, p.y, {
          name: asset.name,
          inner: asset.inner,
          viewBox: asset.viewBox,
          width: asset.width,
          height: asset.height,
          palette: asset.palette,
        });
        return;
      }
    }
    const customRoomId = e.dataTransfer.getData("custom-room-id");
    if (customRoomId) {
      const p = snapPoint(toWorld(e.clientX, e.clientY));
      usePlanner.getState().addCustomRoom(customRoomId, p.x, p.y);
      return;
    }
    const id = e.dataTransfer.getData("catalog");
    if (!id) return;
    const dimsRaw = e.dataTransfer.getData("catalog-dims");
    let overrideW: number | undefined;
    let overrideH: number | undefined;
    if (dimsRaw) {
      try {
        const d = JSON.parse(dimsRaw) as { w?: number; h?: number };
        overrideW = d.w;
        overrideH = d.h;
      } catch {}
    }
    const p = snapPoint(toWorld(e.clientX, e.clientY));
    // Auto-attach to enclosing cluster room (e.g. dropping a chair inside a
    // Director Cabin makes it part of that cabin's cluster).
    const enclosing = scene.items.find(
      (it) => it.kind === "room" && it.groupId && p.x >= it.x && p.x <= it.x + it.w && p.y >= it.y && p.y <= it.y + it.h,
    );
    const dropping = scene.items; // capture for type only
    void dropping;
    addFromCatalog(id, p.x, p.y, { w: overrideW, h: overrideH, groupId: enclosing?.groupId });
    if (enclosing) toast.success(`Added to ${enclosing.label}`);
  }

  function startBpMove(e: React.PointerEvent) {
    e.stopPropagation();
    if (tool !== "select" || !scene.blueprint || scene.blueprint.locked) return;
    pushHistory();
    selectBlueprint(true);
    const bp = scene.blueprint;
    setDrag({ kind: "bp-move", startX: e.clientX, startY: e.clientY, origin: {}, bpOrigin: { x: bp.x, y: bp.y, w: bp.w, h: bp.h } });
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }
  function startBpResize(e: React.PointerEvent, handle: string) {
    e.stopPropagation();
    if (!scene.blueprint || scene.blueprint.locked) return;
    pushHistory();
    const bp = scene.blueprint;
    setDrag({ kind: "bp-resize", startX: e.clientX, startY: e.clientY, origin: {}, handle, bpOrigin: { x: bp.x, y: bp.y, w: bp.w, h: bp.h } });
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }

  // Render — pan is purely transform-based, so the world is effectively infinite.
  const gridMajor = gridSize * 6;
  const GRID_EXTENT = 100000; // huge so the grid never appears to end while panning

  return (
    <div
      className="relative w-full h-full bg-[var(--canvas-bg)] overflow-hidden"
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <svg
        ref={svgRef}
        id="floor-planner-svg"
        className="block w-full h-full"
        style={{ cursor: tool === "pan" ? "grab" : tool === "wall" || tool === "measure" || tool === "area" ? "crosshair" : "default", touchAction: "none" }}
        data-tool={tool}
        onWheel={onWheel}
        onPointerDown={onPointerDownBg}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onDoubleClick={onDoubleClickBg}
      >
        <defs>
          <pattern id="grid-minor" width={gridSize} height={gridSize} patternUnits="userSpaceOnUse">
            <path d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`} fill="none" stroke="var(--grid-line)" strokeWidth={0.5} />
          </pattern>
          <pattern id="grid-major" width={gridMajor} height={gridMajor} patternUnits="userSpaceOnUse">
            <rect width={gridMajor} height={gridMajor} fill="url(#grid-minor)" />
            <path d={`M ${gridMajor} 0 L 0 0 0 ${gridMajor}`} fill="none" stroke="var(--grid-line-strong)" strokeWidth={1} />
          </pattern>
          <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--measure-color)" />
          </marker>
          <marker id="arrow-dark" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--furniture-stroke)" />
          </marker>
        </defs>

        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {layers.grid && (
            <rect x={-GRID_EXTENT} y={-GRID_EXTENT} width={GRID_EXTENT * 2} height={GRID_EXTENT * 2} fill="url(#grid-major)" />
          )}

          {/* Blueprint underlay */}
          {mounted && scene.blueprint && layers.blueprint && (
            <g transform={`translate(${scene.blueprint.x}, ${scene.blueprint.y}) rotate(${scene.blueprint.rotation}, ${scene.blueprint.w / 2}, ${scene.blueprint.h / 2})`}>
              <image
                href={scene.blueprint.src}
                width={scene.blueprint.w}
                height={scene.blueprint.h}
                opacity={scene.blueprint.opacity}
                onPointerDown={(e) => {
                  startBpMove(e);
                }}
                style={{
                  cursor: scene.blueprint.locked || tool !== "select" ? "default" : "move",
                  pointerEvents: scene.blueprint.locked || tool !== "select" ? "none" : "auto",
                }}
                preserveAspectRatio="none"
              />
              {blueprintSelected && !scene.blueprint.locked && (
                <g>
                  <rect width={scene.blueprint.w} height={scene.blueprint.h} fill="none" stroke="var(--selection)" strokeWidth={2} strokeDasharray="6 4" pointerEvents="none" />
                  {(["nw", "ne", "se", "sw"] as const).map((h) => {
                    const hx = h.includes("w") ? 0 : scene.blueprint!.w;
                    const hy = h.includes("n") ? 0 : scene.blueprint!.h;
                    return (
                      <rect key={h} x={hx - 7} y={hy - 7} width={14} height={14} fill="hsl(var(--background))" stroke="var(--selection)" strokeWidth={2} style={{ cursor: `${h}-resize` }} onPointerDown={(e) => startBpResize(e, h)} />
                    );
                  })}
                </g>
              )}
            </g>
          )}

          {/* Rooms first */}
          {mounted && layers.rooms && scene.items
            .filter((i) => i.kind === "room")
            .map((it) => renderItem(it))}

          {/* Walls */}
          {mounted && layers.walls && scene.walls.map((w) => {
            const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
            const mx = (w.x1 + w.x2) / 2;
            const my = (w.y1 + w.y2) / 2;
            const angle = (Math.atan2(w.y2 - w.y1, w.x2 - w.x1) * 180) / Math.PI;
            const wallSelected = selectedIds.includes(w.id);
            return (
              <g
                key={w.id}
                onDoubleClick={() => eraseWallWithWarn(w.id)}
                onPointerDown={(e) => {
                  if (tool === "erase") { e.stopPropagation(); eraseWallWithWarn(w.id); return; }
                  if (tool === "select") {
                    e.stopPropagation();
                    toggleSelect(w.id, e.shiftKey);
                  }
                }}
                style={{ cursor: tool === "erase" ? "not-allowed" : "pointer" }}
              >
                {/* Transparent wide hit-area so thin walls are easy to click/select. */}
                <line x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2} stroke="transparent" strokeWidth={Math.max(w.thickness, 16)} strokeLinecap="round" />
                <line x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2} stroke={wallStroke(w.material)} strokeWidth={w.thickness} strokeLinecap="round" strokeOpacity={w.material === "glass" ? 0.7 : 1} pointerEvents="none" />
                <g transform={`translate(${mx}, ${my}) rotate(${angle})`}>
                  <rect x={-30} y={-22} width={60} height={14} fill="hsl(var(--background))" stroke="hsl(var(--border))" rx={3} />
                  <text x={0} y={-12} textAnchor="middle" fontSize={10} fill="hsl(var(--foreground))">{formatDistance(len, unit)}</text>
                </g>
                {wallSelected && tool === "select" && (
                  <g>
                    <line x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2} stroke="var(--selection)" strokeWidth={2} strokeDasharray="4 3" pointerEvents="none" />
                    {(["a", "b"] as const).map((end) => {
                      const hx = end === "a" ? w.x1 : w.x2;
                      const hy = end === "a" ? w.y1 : w.y2;
                      return (
                        <circle
                          key={end}
                          cx={hx}
                          cy={hy}
                          r={7}
                          fill="hsl(var(--background))"
                          stroke="var(--selection)"
                          strokeWidth={2}
                          style={{ cursor: "move" }}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            pushHistory();
                            setDrag({
                              kind: "wall-endpoint",
                              startX: e.clientX,
                              startY: e.clientY,
                              origin: {},
                              wallOrigin: { id: w.id, x1: w.x1, y1: w.y1, x2: w.x2, y2: w.y2, end },
                            });
                            (e.target as Element).setPointerCapture?.(e.pointerId);
                          }}
                        />
                      );
                    })}
                  </g>
                )}
              </g>
            );
          })}

          {/* Non-room items */}
          {mounted && layers.items && scene.items
            .filter((i) => i.kind !== "room")
            .map((it) => renderItem(it))}

          {/* Measurements */}
          {mounted && layers.measurements && scene.measurements.map((m) => {
            const len = Math.hypot(m.x2 - m.x1, m.y2 - m.y1);
            const mx = (m.x1 + m.x2) / 2;
            const my = (m.y1 + m.y2) / 2;
            const angle = (Math.atan2(m.y2 - m.y1, m.x2 - m.x1) * 180) / Math.PI;
            return (
              <g
                key={m.id}
                onDoubleClick={() => removeMeasurement(m.id)}
                onPointerDown={(e) => { if (tool === "erase") { e.stopPropagation(); removeMeasurement(m.id); } }}
                style={{ cursor: tool === "erase" ? "not-allowed" : "pointer" }}
              >
                <line x1={m.x1} y1={m.y1} x2={m.x2} y2={m.y2} stroke="var(--measure-color)" strokeWidth={1.5} strokeDasharray="6 4" markerStart="url(#arrow)" markerEnd="url(#arrow)" />
                <g transform={`translate(${mx}, ${my}) rotate(${angle})`}>
                  <rect x={-32} y={-20} width={64} height={14} fill="hsl(var(--background))" stroke="var(--measure-color)" rx={3} />
                  <text x={0} y={-10} textAnchor="middle" fontSize={10} fill="var(--measure-color)">{formatDistance(len, unit)}</text>
                </g>
              </g>
            );
          })}

          {/* Areas */}
          {mounted && layers.areas && scene.areas.map((a) => {
            const d = a.points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ") + " Z";
            const c = polygonCentroid(a.points);
            const area = polygonArea(a.points);
            const peri = polygonPerimeter(a.points);
            return (
              <g
                key={a.id}
                onDoubleClick={() => removeArea(a.id)}
                onPointerDown={(e) => { if (tool === "erase") { e.stopPropagation(); removeArea(a.id); } }}
                style={{ cursor: tool === "erase" ? "not-allowed" : "pointer" }}
              >
                <path d={d} fill="var(--measure-color)" fillOpacity={0.08} stroke="var(--measure-color)" strokeWidth={1.2} strokeDasharray="4 3" />
                <g transform={`translate(${c.x}, ${c.y})`} pointerEvents="none">
                  <rect x={-46} y={-18} width={92} height={34} fill="hsl(var(--background))" stroke="var(--measure-color)" rx={4} />
                  <text x={0} y={-4} textAnchor="middle" fontSize={11} fontWeight={600} fill="var(--measure-color)">{formatArea(area, unit)}</text>
                  <text x={0} y={10} textAnchor="middle" fontSize={9} fill="hsl(var(--foreground))" opacity={0.7}>P: {formatDistance(peri, unit)}</text>
                </g>
              </g>
            );
          })}

          {/* Pending wall preview */}
          {pendingPoint && cursor && (tool === "wall" || tool === "measure") && (() => {
            const raw = snapPoint(cursor);
            const end = angleSnap(pendingPoint, raw, shiftHeld);
            const isWall = tool === "wall";
            return (
              <line
                x1={pendingPoint.x}
                y1={pendingPoint.y}
                x2={end.x}
                y2={end.y}
                stroke={isWall ? wallStroke(wallDraft.material) : "var(--measure-color)"}
                strokeWidth={isWall ? 6 : 1}
                strokeOpacity={isWall ? 0.4 : 0.6}
                strokeDasharray={isWall ? "6 4" : "4 4"}
              />
            );
          })()}

          {/* Pending area preview */}
          {tool === "area" && pendingAreaPoints.length > 0 && (
            <g pointerEvents="none">
              <polyline
                points={pendingAreaPoints.map((p) => `${p.x},${p.y}`).join(" ") + (cursor ? ` ${snapVal(cursor.x)},${snapVal(cursor.y)}` : "")}
                fill="var(--measure-color)"
                fillOpacity={0.05}
                stroke="var(--measure-color)"
                strokeWidth={1.2}
                strokeDasharray="4 3"
              />
              {pendingAreaPoints.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r={4} fill="hsl(var(--background))" stroke="var(--measure-color)" strokeWidth={1.5} />
              ))}
            </g>
          )}

          {/* Wall candidates preview */}
          {wallCandidates && wallCandidates.length > 0 && (
            <g pointerEvents="none">
              {wallCandidates.map((w, i) => (
                <line
                  key={i}
                  x1={w.x1}
                  y1={w.y1}
                  x2={w.x2}
                  y2={w.y2}
                  stroke="oklch(0.65 0.22 0)"
                  strokeWidth={w.thickness}
                  strokeOpacity={0.55}
                  strokeLinecap="round"
                />
              ))}
            </g>
          )}
          {marquee && (
            <rect
              x={Math.min(marquee.x0, marquee.x1)}
              y={Math.min(marquee.y0, marquee.y1)}
              width={Math.abs(marquee.x1 - marquee.x0)}
              height={Math.abs(marquee.y1 - marquee.y0)}
              fill="var(--selection)"
              fillOpacity={0.08}
              stroke="var(--selection)"
              strokeWidth={1 / zoom}
              strokeDasharray={`${4 / zoom} ${3 / zoom}`}
              pointerEvents="none"
            />
          )}
        </g>
      </svg>
      {tool === "area" && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-card border border-border rounded-md shadow px-3 py-1.5 text-xs text-foreground/80 pointer-events-none">
          Click to add points · double-click or Enter to finish · Esc to cancel
          {pendingAreaPoints.length > 0 && <span className="ml-2 text-muted-foreground">({pendingAreaPoints.length} pts)</span>}
        </div>
      )}
      <ClusterEditor
        open={!!clusterEditorRoomId}
        roomId={clusterEditorRoomId}
        onOpenChange={(v) => { if (!v) setClusterEditorRoomId(null); }}
      />
    </div>
  );

  function renderItem(it: (typeof scene.items)[number]) {
    const selected = selectedIds.includes(it.id);
    const cx = it.x + it.w / 2;
    const cy = it.y + it.h / 2;
    function onItemDoubleClick(e: React.MouseEvent) {
      if (tool !== "select") return;
      // Find the cluster room for this item (either it IS the room, or it's a child).
      let roomId: string | null = null;
      if (it.kind === "room" && it.groupId) roomId = it.id;
      else if (it.groupId) {
        const room = scene.items.find((x) => x.groupId === it.groupId && x.kind === "room");
        if (room) roomId = room.id;
      }
      if (roomId) {
        e.stopPropagation();
        setClusterEditorRoomId(roomId);
      }
    }
    return (
      <g key={it.id} transform={`translate(${it.x}, ${it.y}) rotate(${it.rotation}, ${it.w / 2}, ${it.h / 2})`}>
        <g onPointerDown={(e) => startMove(e, it.id)} onDoubleClick={onItemDoubleClick} className="cursor-move">
          <ItemShape item={it} />
          {(() => {
            const team = teamFor(it, scene);
            if (!team) return null;
            // Team color tint overlay for cabins & seats.
            return (
              <rect
                width={it.w}
                height={it.h}
                fill={team.color}
                opacity={it.kind === "room" ? 0.18 : 0.35}
                pointerEvents="none"
                rx={it.shape === "chair" ? 6 : 2}
              />
            );
          })()}
          {(() => {
            // Allocation badge: seat code + assignee on chairs, assignee on rooms.
            const code = it.shape === "chair" ? seatCodeFor(it, scene) : undefined;
            const name = it.assignee;
            if (!code && !name) return null;
            const lines = [code, name].filter(Boolean) as string[];
            const w = Math.max(36, lines.reduce((m, l) => Math.max(m, l.length * 5.2 + 10), 0));
            const h = lines.length * 11 + 4;
            const x = it.w / 2 - w / 2;
            const y = it.kind === "room" ? 28 : it.h / 2 - h / 2;
            return (
              <g transform={`translate(${x}, ${y})`} pointerEvents="none">
                <rect width={w} height={h} fill="hsl(var(--background))" stroke="var(--furniture-stroke)" strokeOpacity={0.4} rx={3} />
                {lines.map((l, i) => (
                  <text
                    key={i}
                    x={w / 2}
                    y={11 + i * 11}
                    textAnchor="middle"
                    fontSize={9}
                    fontWeight={i === 0 && code ? 700 : 500}
                    fill="hsl(var(--foreground))"
                  >
                    {l}
                  </text>
                ))}
              </g>
            );
          })()}
        </g>
        {selected && selectedIds.length === 1 && (
          <g>
            <rect width={it.w} height={it.h} fill="none" stroke="var(--selection)" strokeWidth={1.5} strokeDasharray="4 3" pointerEvents="none" />
            {/* resize handles */}
            {(["nw", "n", "ne", "e", "se", "s", "sw", "w"] as const).map((h) => {
              const hx = h.includes("w") ? 0 : h.includes("e") ? it.w : it.w / 2;
              const hy = h.includes("n") ? 0 : h.includes("s") ? it.h : it.h / 2;
              return (
                <rect
                  key={h}
                  x={hx - 5}
                  y={hy - 5}
                  width={10}
                  height={10}
                  fill="hsl(var(--background))"
                  stroke="var(--selection)"
                  strokeWidth={1.5}
                  style={{ cursor: `${h}-resize` }}
                  onPointerDown={(e) => startResize(e, it.id, h)}
                />
              );
            })}
            {/* rotate handle */}
            <line x1={it.w / 2} y1={-30} x2={it.w / 2} y2={0} stroke="var(--selection)" strokeWidth={1} />
            <circle
              cx={it.w / 2}
              cy={-30}
              r={6}
              fill="var(--selection)"
              style={{ cursor: "grab" }}
              onPointerDown={(e) => startRotate(e, it.id)}
            />
          </g>
        )}
        {selected && selectedIds.length > 1 && (
          <rect width={it.w} height={it.h} fill="none" stroke="var(--selection)" strokeWidth={1.2} strokeDasharray="3 3" pointerEvents="none" />
        )}
        {/* invisible cx/cy ref to avoid lint */}
        <g style={{ display: "none" }}>{cx}{cy}</g>
      </g>
    );
  }
}

function getSceneBounds(scene: ReturnType<typeof usePlanner.getState>["scene"]) {
  const boxes: { minX: number; minY: number; maxX: number; maxY: number }[] = [];
  scene.items.forEach((it) => boxes.push({ minX: it.x, minY: it.y, maxX: it.x + it.w, maxY: it.y + it.h }));
  scene.walls.forEach((w) => boxes.push({ minX: Math.min(w.x1, w.x2), minY: Math.min(w.y1, w.y2), maxX: Math.max(w.x1, w.x2), maxY: Math.max(w.y1, w.y2) }));
  scene.measurements.forEach((m) => boxes.push({ minX: Math.min(m.x1, m.x2), minY: Math.min(m.y1, m.y2), maxX: Math.max(m.x1, m.x2), maxY: Math.max(m.y1, m.y2) }));
  scene.areas.forEach((a) => {
    if (!a.points.length) return;
    boxes.push({
      minX: Math.min(...a.points.map((p) => p.x)),
      minY: Math.min(...a.points.map((p) => p.y)),
      maxX: Math.max(...a.points.map((p) => p.x)),
      maxY: Math.max(...a.points.map((p) => p.y)),
    });
  });
  if (scene.blueprint) boxes.push({ minX: scene.blueprint.x, minY: scene.blueprint.y, maxX: scene.blueprint.x + scene.blueprint.w, maxY: scene.blueprint.y + scene.blueprint.h });
  if (!boxes.length) return null;
  return {
    minX: Math.min(...boxes.map((b) => b.minX)),
    minY: Math.min(...boxes.map((b) => b.minY)),
    maxX: Math.max(...boxes.map((b) => b.maxX)),
    maxY: Math.max(...boxes.map((b) => b.maxY)),
  };
}